import axios from "axios";
import React from "react";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { BsCartCheck } from "react-icons/bs";
import { FiShoppingCart } from "react-icons/fi";
import { NavLink } from "react-router-dom";
import Swal from "sweetalert2";
import Empty from "../../assets/images/appImages/category.png";
import { useAppDispatch, useAppSelector } from "../../redux/hook";
import {
  addCartProduct,
  getCartCount,
  getTotalAmount,
  removeCartItem,
  type CartItemType,
} from "../../redux/slice/useCartSlice";
import { formatCurrency, getImage } from "../../utils/Utils";
import { useLazyFavoriteByUserQuery } from "../../utils/api/favories/favorie.api";
import { useGetVendeurByApiKeyQuery } from "../../utils/api/product/product.api";
import { ApiBaseUrl, VendeurApikey } from "../../utils/http";
import { Color } from "../../utils/theme";
import {
  effectiveStockForCartItem,
  isProductAvailableForPurchase,
  variationsForCartPayload,
} from "../../utils/productStock";

interface BestSellerCardProps {
  item: any;
  onFavoriteToggle?: () => void;
}

/**
 * Carte produit style Yeksina : image, badge promo, nom, prix, bouton Acheter, icône panier, favoris.
 * Couleurs du site via var(--primary-color).
 */
export const BestSellerCard: React.FC<BestSellerCardProps> = ({
  item,
  onFavoriteToggle,
}) => {
  const dispatch = useAppDispatch();
  const { user, token } = useAppSelector((s) => s?.user);
  const { cartItems } = useAppSelector((s) => s?.cart);
  const { data: vendeur } = useGetVendeurByApiKeyQuery(VendeurApikey);
  const Vendeur = vendeur || { devise: "XOF" };

  const imgProd =
    item?.type === "taille_unique"
      ? item?.images?.[0]?.image
      : item?.variations?.[0]?.images?.[0]?.image ?? null;

  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const [findBySlug, { data, isLoading }] = useLazyFavoriteByUserQuery();

  React.useEffect(() => {
    if (user?.slug) {
      findBySlug(user?.slug as string);
    }
  }, [user?.slug, findBySlug]);

  const handleAddFavoris = (id: number | string) => {
    const favorie = data?.find((x) => x?.produit?.id == id);
    const fd = new FormData();
    fd.append("user", String(user?.id ?? ""));
    fd.append("produit", String(id));

    if (favorie) {
      axios
        .delete(ApiBaseUrl + `/api/favoris/${favorie?.slug}`, config)
        .then(() => {
          findBySlug(user?.slug as string);
          onFavoriteToggle?.();
        })
        .catch((err) => {
          console.error(err);
        });
    } else {
      axios
        .post(ApiBaseUrl + "/api/favoris/", fd, config)
        .then(() => {
          findBySlug(user?.slug as string);
          onFavoriteToggle?.();
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  const isFavorite = (id: number | string): boolean =>
    !!data?.find((x) => x?.produit?.id == id);

  const onToggleFavorite = (id: number | string) => {
    if (!user?.slug) {
      Swal.fire({
        icon: "info",
        title: "Veuillez vous connecter pour ajouter un produit aux favoris.",
        iconColor: Color.success,
        showConfirmButton: false,
        timer: 6000,
      });
    } else {
      handleAddFavoris(id);
    }
  };

  const reductionTaux =
    item?.categorie?.promotion?.taux ||
    item?.sous_categorie?.promotion?.taux ||
    item?.promotion?.taux;

  const hasPromotion =
    item?.categorie?.promotion?.is_active ||
    item?.sous_categorie?.promotion?.is_active ||
    item?.promotion?.is_active;

  const prixFinal = hasPromotion
    ? item?.prix - (item?.prix * reductionTaux) / 100
    : item?.prix;

  const cartVariations = variationsForCartPayload(item);

  const isInCart = (): boolean =>
    (cartItems?.some(
      (cartItem: CartItemType) =>
        cartItem?.produit?.id === item?.id &&
        (cartItem?.variations?.[0]?.id ?? null) === (cartVariations?.[0]?.id ?? null)
    ) ?? false);

  const getCartItemForProduct = (): CartItemType | undefined =>
    cartItems?.find(
      (cartItem: CartItemType) =>
        cartItem?.produit?.id === item?.id &&
        (cartItem?.variations?.[0]?.id ?? null) === (cartVariations?.[0]?.id ?? null)
    );

  const handleToggleCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isProductAvailableForPurchase(item)) return;

    if (isInCart()) {
      const cartItem = getCartItemForProduct();
      if (cartItem) {
        dispatch(removeCartItem(cartItem));
        dispatch(getCartCount());
        dispatch(getTotalAmount());
        Swal.fire({
          icon: "info",
          iconColor: Color.success,
          confirmButtonColor: Color.success,
          title: "Produit retiré du panier",
          showConfirmButton: false,
          timer: 2000,
        });
      }
      return;
    }

    const produit: CartItemType = {
      produit: item,
      prix: prixFinal,
      quantite: 1,
      stock: effectiveStockForCartItem(item),
      variations: cartVariations,
      vendeur: item?.vendeur?.id,
    };

    dispatch(addCartProduct(produit));
    Swal.fire({
      icon: "success",
      iconColor: Color.success,
      confirmButtonColor: Color.success,
      title: "Produit ajouté au panier avec succès !",
      showConfirmButton: false,
      timer: 2000,
    });
  };

  const canPurchase = isProductAvailableForPurchase(item);
  const inCart = isInCart();

  return (
    <div className="best-seller-card">
      <NavLink
        to={`/produit/${item?.slug}`}
        state={item}
        className="best-seller-card-link"
      >
        <div className="best-seller-card-image-wrapper">
          {hasPromotion && reductionTaux && (
            <div className="best-seller-card-badge-right">
              -{reductionTaux}%
            </div>
          )}
          <img
            loading="lazy"
            src={imgProd ? getImage(imgProd) : Empty}
            alt={item?.nom ?? ""}
            className="best-seller-card-image"
          />
          <button
            disabled={isLoading}
            className="best-seller-card-favorite"
            aria-label="Ajouter aux favoris"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleFavorite(item?.id);
            }}
          >
            {isFavorite(item?.id) ? (
              <AiFillHeart className="best-seller-card-favorite-icon best-seller-card-favorite-icon-filled" />
            ) : (
              <AiOutlineHeart className="best-seller-card-favorite-icon" />
            )}
          </button>

          <button
            className={`best-seller-card-cart-icon-mobile ${inCart ? "best-seller-card-cart-icon-in-cart" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleToggleCart(e);
            }}
            aria-label={inCart ? "Retirer du panier" : "Ajouter au panier"}
            title={inCart ? "Retirer du panier" : "Ajouter au panier"}
            disabled={!canPurchase}
          >
            {inCart ? <BsCartCheck /> : <FiShoppingCart />}
          </button>
        </div>

        <div className="best-seller-card-body">
          <h4 className="best-seller-card-name">{item?.nom}</h4>

          <div className="best-seller-card-price">
            {hasPromotion ? (
              <>
                <span className="best-seller-card-price-final">
                  {formatCurrency(prixFinal, Vendeur?.devise)}
                </span>
                <span className="best-seller-card-price-original">
                  {formatCurrency(item?.prix, Vendeur?.devise)}
                </span>
              </>
            ) : (
              <span className="best-seller-card-price-final">
                {formatCurrency(item?.prix, Vendeur?.devise)}
              </span>
            )}
          </div>

          <div className="best-seller-card-button-wrapper">
            {canPurchase ? (
              <>
                <NavLink
                  to={`/produit/${item?.slug}`}
                  className="best-seller-card-button"
                  onClick={(e) => e.stopPropagation()}
                >
                  Acheter
                </NavLink>
                <button
                  className={`best-seller-card-cart-icon best-seller-card-cart-icon-desktop ${inCart ? "best-seller-card-cart-icon-in-cart" : ""}`}
                  onClick={handleToggleCart}
                  aria-label={inCart ? "Retirer du panier" : "Ajouter au panier"}
                  title={inCart ? "Retirer du panier" : "Ajouter au panier"}
                >
                  {inCart ? <BsCartCheck /> : <FiShoppingCart />}
                </button>
              </>
            ) : (
              <>
                <button
                  className="best-seller-card-button best-seller-card-button-disabled"
                  disabled
                >
                  Acheter
                </button>
                <button
                  className="best-seller-card-cart-icon best-seller-card-cart-icon-desktop best-seller-card-cart-icon-disabled"
                  disabled
                  aria-label="Produit en rupture de stock"
                  title="Produit en rupture de stock"
                >
                  <FiShoppingCart />
                </button>
              </>
            )}
          </div>
        </div>
      </NavLink>
    </div>
  );
};
